import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { getTodayDateString } from '@/lib/utils';
import { useCreateJobPayment } from '../hooks/useCreateJobPayment';
import { useUpdateJobPayment } from '../hooks/useUpdateJobPayment';
import { useJobEntryDuplicateCheck } from '../hooks/useJobEntryDuplicateCheck';
import { uploadPaymentImages } from '../api/paymentImages';
import { PaymentImagesSection } from './PaymentImagesSection';
import { DuplicateEntryWarningDialog } from './DuplicateEntryWarningDialog';
import type { JobPayment } from '../types/jobPayment';
import type { PotentialJobEntryDuplicate } from '../api/checkJobEntryDuplicates';

/**
 * Static list of payment types matching database seed data
 */
const PAYMENT_TYPES = [
  { id: 1, name: 'Check' },
  { id: 2, name: 'Cash' },
  { id: 3, name: 'Credit Card' },
  { id: 4, name: 'Wire Transfer' },
  { id: 5, name: 'Insurance Check' },
  { id: 6, name: 'Other' },
];

interface PaymentEntryFormProps {
  jobId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingPayment?: JobPayment | null;
  isForecast: boolean;
}

export function PaymentEntryForm({
  jobId,
  isOpen,
  onClose,
  onSuccess,
  editingPayment,
  isForecast,
}: PaymentEntryFormProps) {
  const { create, isCreating, error: createError } = useCreateJobPayment();
  const { update, isUpdating, error: updateError } = useUpdateJobPayment();
  const { checkForPaymentDuplicates, isChecking } = useJobEntryDuplicateCheck();

  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicates, setDuplicates] = useState<PotentialJobEntryDuplicate[]>([]);
  const [duplicatesAcknowledged, setDuplicatesAcknowledged] = useState(false);

  // Form state
  const [paymentTypeId, setPaymentTypeId] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState('');
  const [amount, setAmount] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [isInterest, setIsInterest] = useState(false);
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const error = createError || updateError || validationError;
  const isSubmitting = isCreating || isUpdating || isChecking || isUploadingFiles;

  // Get reference number label based on payment type
  const getReferenceNumberLabel = () => {
    const selectedType = PAYMENT_TYPES.find((t) => t.id === parseInt(paymentTypeId));
    if (!selectedType) return 'Reference Number';

    switch (selectedType.name.toLowerCase()) {
      case 'check':
      case 'insurance check':
        return 'Check Number';
      default:
        return 'Transaction ID';
    }
  };

  // Initialize form when dialog opens or editingPayment changes
  useEffect(() => {
    // This effect intentionally sets state to initialize/reset the form based on props.
    // This is a valid use case for form initialization and doesn't cause cascading renders.
    if (isOpen) {
      if (editingPayment) {
        // Populate form with existing payment data
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPaymentTypeId(editingPayment.paymentTypeId.toString());

        setPaymentDate(editingPayment.paymentDate.split('T')[0]);

        setAmount(editingPayment.amount.toString());

        setReferenceNumber(editingPayment.referenceNumber || '');

        setIsPending(editingPayment.isPending);

        setIsInterest(editingPayment.isInterest);

        setNotes(editingPayment.notes || '');
      } else {
        // Reset form for new payment
        setPaymentTypeId('');
        setPaymentDate(getTodayDateString());
        setAmount('');
        setReferenceNumber('');
        setIsPending(false);
        setIsInterest(false);
        setNotes('');
      }

      setValidationError('');
      setDuplicatesAcknowledged(false);
      setDuplicates([]);
      setPendingFiles([]);
    }
  }, [isOpen, editingPayment]);

  const validateForm = (): boolean => {
    if (!paymentTypeId) {
      setValidationError('Payment type is required');
      return false;
    }

    if (!paymentDate) {
      setValidationError('Payment date is required');
      return false;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      setValidationError('Amount must be a valid number');
      return false;
    }

    if (amountNum <= 0) {
      setValidationError('Amount must be positive');
      return false;
    }

    if (referenceNumber && referenceNumber.length > 50) {
      setValidationError('Reference number must be 50 characters or less');
      return false;
    }

    if (notes && notes.length > 500) {
      setValidationError('Notes must be 500 characters or less');
      return false;
    }

    return true;
  };

  const performSave = async () => {
    const data = {
      paymentTypeId: parseInt(paymentTypeId),
      isForecast,
      paymentDate: new Date(paymentDate).toISOString(),
      amount: parseFloat(amount),
      referenceNumber: referenceNumber.trim() || undefined,
      isPending,
      isInterest,
      notes: notes.trim() || undefined,
    };

    let result;
    if (editingPayment) {
      result = await update(jobId, editingPayment.id, data);
    } else {
      result = await create(jobId, data);
    }

    if (result) {
      // Upload pending files after successful creation
      if (!editingPayment && pendingFiles.length > 0) {
        setIsUploadingFiles(true);
        try {
          await uploadPaymentImages(jobId, result.id, pendingFiles);
        } catch (err) {
          console.error('Failed to upload images after payment creation:', err);
        } finally {
          setIsUploadingFiles(false);
        }
      }
      onSuccess();
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!validateForm()) {
      return;
    }

    // If duplicates were already acknowledged, proceed with save
    if (duplicatesAcknowledged) {
      await performSave();
      return;
    }

    // Check for duplicates before saving
    const duplicateResult = await checkForPaymentDuplicates(jobId, {
      amount: parseFloat(amount),
      referenceNumber: referenceNumber.trim() || undefined,
      paymentDate: new Date(paymentDate).toISOString(),
      excludePaymentId: editingPayment?.id,
    });

    if (duplicateResult.hasDuplicates) {
      setDuplicates(duplicateResult.duplicates);
      setShowDuplicateDialog(true);
      return;
    }

    // No duplicates, proceed with save
    await performSave();
  };

  const handleProceedWithDuplicates = async () => {
    setDuplicatesAcknowledged(true);
    setShowDuplicateDialog(false);
    await performSave();
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateDialog(false);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingPayment ? 'Edit Payment' : 'Add Payment'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <ErrorAlert error={error} />

          <div className="space-y-2">
            <Label htmlFor="paymentType">
              Payment Type <span className="text-red-500">*</span>
            </Label>
            <Select
              id="paymentType"
              value={paymentTypeId}
              onChange={(e) => setPaymentTypeId(e.target.value)}
              autoFocus
            >
              <option value="">Select payment type</option>
              {PAYMENT_TYPES.map((type) => (
                <option key={type.id} value={type.id.toString()}>
                  {type.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentDate">
              Payment Date <span className="text-red-500">*</span>
            </Label>
            <DateInput
              id="paymentDate"
              value={paymentDate || undefined}
              onChange={(iso) => setPaymentDate(iso ?? '')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount <span className="text-red-500">*</span>
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="referenceNumber">{getReferenceNumberLabel()}</Label>
            <Input
              id="referenceNumber"
              type="text"
              placeholder={paymentTypeId && ['1', '5'].includes(paymentTypeId) ? 'Check #' : 'Transaction ID'}
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              maxLength={50}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isPending"
              checked={isPending}
              onChange={(e) => setIsPending(e.target.checked)}
            />
            <div className="space-y-1">
              <Label htmlFor="isPending" className="cursor-pointer">
                Payment is pending
              </Label>
              <p className="text-xs text-gray-500">
                Check this if payment hasn&apos;t cleared yet
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isInterest"
              checked={isInterest}
              onChange={(e) => setIsInterest(e.target.checked)}
            />
            <div className="space-y-1">
              <Label htmlFor="isInterest" className="cursor-pointer">
                This is an interest payment
              </Label>
              <p className="text-xs text-gray-500">
                Check this if this is an interest payment
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Optional notes about this payment"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <p className="text-xs text-gray-500">{notes.length} / 500 characters</p>
          </div>

          {/* Document section */}
          <div className="border-t border-gray-200 my-4 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Payment Documents
            </h3>
            {editingPayment ? (
              <PaymentImagesSection
                jobId={jobId}
                paymentId={editingPayment.id}
                canDelete={true}
              />
            ) : (
              <>
                <ImageUpload
                  onUpload={async (files) => setPendingFiles((prev) => [...prev, ...files])}
                  isUploading={false}
                  compact
                  immediate
                />
                {pendingFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {pendingFiles.length} file(s) will be uploaded on save
                    </p>
                    {pendingFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          className="ml-2 text-muted-foreground hover:text-foreground"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-start gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : editingPayment
                  ? 'Update Payment'
                  : 'Add Payment'}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Duplicate Warning Dialog */}
      <DuplicateEntryWarningDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        entryType="Payment"
        duplicates={duplicates}
        onProceed={handleProceedWithDuplicates}
        onCancel={handleCancelDuplicates}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
}
